import { useState } from 'react'
import { ArrowLeft, Clock3, GitCommitHorizontal } from 'lucide-react'
import { Button } from '@/components/ui/button'
import RepositoryFilePreview from '@/frontend/features/Fde/RepositoryFilePreview'
import WorkspaceFileTabs from '@/frontend/features/Fde/WorkspaceFileTabs'
import WorkspaceVersionHistory from '@/frontend/features/Fde/WorkspaceVersionHistory'
import { WorkspaceFileIcon, formatFileSize } from '@/frontend/features/Fde/WorkspaceFileVisual'
import type { FdeCopy, RepositoryCommit, RepositoryFile } from '@/frontend/features/Fde/types'

export default function WorkspaceFilePanel({ projectId, file, openFiles, repositoryRevision, selectedRevision, versions, copy, editable, onBack, onSelect, onClose }: {
  projectId: string
  file: RepositoryFile
  openFiles: RepositoryFile[]
  repositoryRevision: string | null
  selectedRevision: string | null
  versions: RepositoryCommit[]
  copy: FdeCopy
  editable: boolean
  onBack: () => void
  onSelect: (path: string) => void
  onClose: (path: string) => void
}) {
  const [historyOpen, setHistoryOpen] = useState(false)
  const displayedCommit = selectedRevision ? versions.find((version) => version.sha === selectedRevision) : file.latest_commit
  const author = displayedCommit?.author_name || copy.material.unknown_author

  return <section className="flex h-full min-h-0 flex-col overflow-hidden bg-background">
    <WorkspaceFileTabs files={openFiles} activePath={file.path} onSelect={onSelect} onClose={onClose} closeLabel={copy.common.close} />
    <header className="flex min-h-16 shrink-0 items-center gap-3 border-b border-border bg-card px-4">
      <Button size="icon-sm" variant="ghost" onClick={onBack} aria-label={copy.material.back_to_files}><ArrowLeft /></Button>
      <WorkspaceFileIcon file={file} />
      <div className="min-w-0 flex-1"><div className="flex items-center gap-2"><h2 className="truncate text-sm font-semibold text-foreground">{file.name}</h2>{file.historical && <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-700">{copy.material.historical_version}</span>}</div><p className="mt-0.5 truncate text-[11px] text-muted-foreground">{file.path} · {formatFileSize(file.byte_size)} · {copy.material.updated_by_name.replace('%{name}', author)}</p></div>
      <Button size="sm" variant={historyOpen ? 'secondary' : 'outline'} onClick={() => setHistoryOpen((current) => !current)}><Clock3 />{copy.material.versions}<span className="rounded bg-muted px-1.5 text-[10px]">{file.version_count}</span></Button>
    </header>
    {file.historical && <div className="flex shrink-0 items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900"><GitCommitHorizontal className="size-4" />{copy.material.viewing_commit.replace('%{sha}', (file.viewed_revision || '').slice(0, 7))}</div>}
    <div className="flex min-h-0 flex-1 overflow-hidden">
      <div className="min-w-0 flex-1"><RepositoryFilePreview key={`${file.path}:${file.viewed_revision || repositoryRevision}`} file={file} copy={copy} projectId={projectId} repositoryRevision={repositoryRevision} editable={editable && !file.historical && file.editable} /></div>
      {historyOpen && <WorkspaceVersionHistory projectId={projectId} file={file} versions={versions} selectedRevision={selectedRevision} copy={copy} onClose={() => setHistoryOpen(false)} />}
    </div>
  </section>
}
